import { useHistory } from "react-router-dom";
import { CustomField, CustomFieldFilters, CustomFieldFiltersTranslator } from "../../../api/CustomFields/CustomFields";
import { ColumnsSettings } from "../../../common/tables/ColumnsSelector/ColumnsSelector";
import {
  GenericVirtualizedTableCells,
  SortState,
} from "../../../common/tables/GenericVirtualizedTable/GenericVirtualizedTableTypes";
import { useMemo, useState } from "react";
import { CustomFieldFilterForm, CustomFieldsFilterBar } from "./CustomFieldsFilterBar";
import { TableTabsDict } from "../../../common/tables/Tabs/TableTabsTypes";
import { useTabStore } from "../../../common/tables/Tabs/useTabStore";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import { DateTimeRenderer } from "../../../common/datetime/DateTimeFormatter";
import { GetPersons, TableArrayRenderer } from "../../../common/misc/EntityRenders/EntityRenderer";
import TableView, { TableViewLabel } from "../../../common/panels/TableView/TableView";
import { EntityTableTabs } from "../../../common/tables/Tabs/EntityTableTabs";
import { EntityTable } from "../../../common/tables/EntityTable/EntityTable";
import { EntityFilterIndicator } from "../../../common/tables/EntityFilterIndicator/EntityFilterIndicator";
import { SearchInput } from "../../../common/forms/SearchInput/SearchInput";
import { MoreDropdown } from "../../../common/buttons/MoreDropdown/MoreDropdown";
import { AlertModal } from "../../../common/modals/AlertModal/AlertModal";
import styles from "../../../common/tables/GenericVirtualizedTable/commons.module.css";
import { AbstractedCustomFieldDataTypeIcon } from "../CustomFieldRenderUtils";
import { generateAbstractedFromDataType } from "../CustomFieldUtils";
import { NotSet } from "../../../common/misc/UIconstants";
import {
  genericDefaultSortState,
  getAsyncSortingFn,
  getSortStateArray,
  UseEntityTableColumns,
  UseEntityTableDefaultProps,
  UseEntityTableProps,
  useGenericVirtualizedTable,
  useGenericVirtualizedTableTabs,
} from "../../../common/tables/GenericVirtualizedTable/useGenericVirtualizedTable";
import { EntityTableProps } from "../../../common/entity/EntityInterfaces";
import { EntityTableDeleteButton } from "../../../common/entity/entityComponents/EntityTableDeleteButton";
import { EntityTableCloneButton } from "../../../common/entity/entityComponents/EntityTableCloneButton";
import { EntityTableEditButton } from "../../../common/entity/entityComponents/EntityTableEditButton";
import { CustomFieldTableRenderer } from "../CustomFieldRenderer";
import { ToggleIndicator } from "../../../common/badges/ToggleIndicator/ToggleIndicator";
import { EntityOrderBy, InferSortByOptions } from "../../../api/GenericTypes";

export const switchCustomFieldsDefaultSortState = (
  sortState: EntityOrderBy<InferSortByOptions<CustomFieldFilters>>[] | null | undefined
): SortState<InferSortByOptions<CustomFieldFilters>>[] => {
  const state = genericDefaultSortState(sortState);
  if (state) return state;

  return getSortStateArray(sortState, (sort) => {
    switch (sort?.property) {
      case "DATATYPE":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-dataType",
          property: sort.property,
        };
      default:
        return {
          sortDirection: "ASC",
          headerId: "default-name",
          property: "NAME",
        };
    }
  });
};
export const defaultCustomFieldsFilters: CustomFieldFilters = {
  sortBy: [{ property: "NAME", sortDirection: "ASC" }],
};

export const useCustomFieldsTableDefaults = ({ fieldLabels }: UseEntityTableDefaultProps<"customFields">) => {
  const defaults: ColumnsSettings<CustomField> = useMemo(
    () => ({
      "default-id": { pos: 0, active: false, header: fieldLabels.id, property: "id" },
      "default-name": { pos: 1, active: true, header: fieldLabels.name, property: "name" },
      "default-dataType": { pos: 2, active: true, header: fieldLabels.dataType, property: "dataType" },
      "default-description": {
        pos: 3,
        active: true,
        header: fieldLabels.description,
        property: "description",
      },
      "default-placeholder": { pos: 4, active: true, header: fieldLabels.placeholder, property: "placeholder" },
      "default-required": { pos: 5, active: true, header: fieldLabels.required, property: "required" },
      "default-readOnly": { pos: 6, active: true, header: fieldLabels.readOnly, property: "readOnly" },
      "default-validationRegexp": {
        pos: 7,
        active: true,
        header: fieldLabels.validationRegexp,
        property: "validationRegexp",
      },
      "default-validationMessage": {
        pos: 8,
        active: true,
        header: fieldLabels.validationMessage,
        property: "validationMessage",
      },
      "default-predefinedOptions": {
        pos: 10,
        active: true,
        header: fieldLabels.predefinedOptions,
        property: "predefinedOptions",
      },
      "default-predefinedOptionsFromValues": {
        pos: 11,
        active: true,
        header: fieldLabels.predefinedOptionsFromValues,
        property: "predefinedOptionsFromValues",
      },
      "default-defaultValues": { pos: 12, active: true, header: fieldLabels.defaultValues, property: "defaultValues" },
      "default-integrations": { pos: 13, active: false, header: fieldLabels.integrations, property: "integrations" },
      "default-enteredOn": { pos: 14, active: true, header: fieldLabels.enteredOn, property: "enteredOn" },
      "default-enteredBy": { pos: 15, active: true, header: fieldLabels.enteredBy, property: "enteredBy" },
      "default-modifiedOn": { pos: 16, active: true, header: fieldLabels.modifiedOn, property: "modifiedOn" },
      "default-modifiedBy": { pos: 17, active: true, header: fieldLabels.modifiedBy, property: "modifiedBy" },
    }),
    [fieldLabels]
  );

  const tabStoreDefaults: TableTabsDict<CustomField, CustomFieldFilters, CustomFieldFilterForm> = useMemo(
    () => ({
      default: {
        tabId: "default",
        type: "fixed",
        label: "All",
        title: "All",
        icon: "house",
        align: "left",
        xPos: 0,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultCustomFieldsFilters,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: {},
          sidebarFilters: {},
        },
      },
      // trash: {
      //   tabId: "trash",
      //   type: "fixed",
      //   label: "Trash",
      //   title: "Trash",
      //   icon: "trash",
      //   align: "left",
      //   xPos: 1,
      //   settings: {
      //     columnSettings: {},
      //     columnWidths: {},
      //     filters: defaultFilter,
      //     sidebarFilters: {},
      //   },
      //   forcedSettings: {
      //     columnSettings: {},
      //     columnWidths: {},
      //     filters: { isSoftDeleted: true },
      //     sidebarFilters: {},
      //   },
      // },
    }),
    []
  );
  return { defaults, tabStoreDefaults };
};

export const useCustomFieldsTableColumns: UseEntityTableColumns<"customFields"> = ({
  entityConstants,
  fieldLabels,
  sort,
  setSort,
}: UseEntityTableProps<"customFields">) => {
  const columns = useMemo(() => {
    let headers: GenericVirtualizedTableCells<CustomField> = [
      {
        id: "default-id",
        Header: fieldLabels.id,
        accessor: (row) => <span style={{ color: "var(--gray-400)" }}>{row.id}</span>,
        width: 120,
        minWidth: 120,
        align: "center",
        ...getAsyncSortingFn("ID", sort, setSort),
      },
      {
        Header: fieldLabels.name,
        id: "default-name",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis} gap-5`}>
            <div className={`${styles.container_ellipsis}`}>
              <span className={styles.label_highlight}>
                <LucideIcon name={entityConstants.icon} color={"var(--primary)"} /> {row.name}
              </span>
            </div>
          </div>
        ),
        minWidth: 150,
        width: 250,
        align: "left",
        ...getAsyncSortingFn("NAME", sort, setSort),
      },
      {
        Header: fieldLabels.dataType,
        id: "default-dataType",
        accessor: (row) => {
          const _type = generateAbstractedFromDataType(row.dataType);
          return (
            <>
              {_type && (
                <div className="flex row-nowrap align-center gap-5">
                  {_type.abstractedDataType && (
                    <>
                      <AbstractedCustomFieldDataTypeIcon dataType={_type.abstractedDataType} />
                      {_type.abstractedDataType}
                      {_type.isMulti && (
                        <label className="label label-soft-info" style={{ margin: 0 }}>
                          Multi
                        </label>
                      )}
                      {_type.isFloat && (
                        <label className="label label-soft-info" style={{ margin: 0 }}>
                          Floating point number
                        </label>
                      )}
                      {_type.isDateTime && (
                        <label className="label label-soft-info" style={{ margin: 0 }}>
                          Date/Time
                        </label>
                      )}
                      {_type.isDateTimeRange && (
                        <label className="label label-soft-info" style={{ margin: 0 }}>
                          Date/Time range
                        </label>
                      )}
                      {_type.isTimeRange && (
                        <label className="label label-soft-info" style={{ margin: 0 }}>
                          Time range
                        </label>
                      )}
                      {row.showAsTextArea && (
                        <label className="label label-soft-info" style={{ margin: 0 }}>
                          Text area
                        </label>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          );
        },
        minWidth: 100,
        width: 250,
        align: "left",
        ...getAsyncSortingFn("DATATYPE", sort, setSort),
      },
      {
        Header: fieldLabels.description,
        id: "default-description",
        accessor: (row) => (
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.description || NotSet}
          </div>
        ),
        minWidth: 100,
        width: 500,
        align: "left",
      },
      {
        Header: fieldLabels.placeholder,
        id: "default-placeholder",
        accessor: (row) => (
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={row.placeholder}
          >
            {row.placeholder || NotSet}
          </div>
        ),
        minWidth: 100,
        width: 300,
        align: "left",
      },
      {
        Header: fieldLabels.required,
        id: "default-required",
        accessor: (row) => <ToggleIndicator enabled={row.required ?? false} labelSet="yes/no" />,
        minWidth: 100,
        width: 100,
        align: "left",
      },
      {
        Header: fieldLabels.readOnly,
        id: "default-readOnly",
        accessor: (row) => <ToggleIndicator enabled={row.readOnly ?? false} labelSet="yes/no" />,
        minWidth: 100,
        width: 100,
        align: "left",
      },
      {
        Header: fieldLabels.validationRegexp,
        id: "default-validationRegexp",
        accessor: (row) => (
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={row.validationRegexp}
          >
            {row.validationRegexp || NotSet}
          </div>
        ),
        minWidth: 100,
        width: 300,
        align: "left",
      },
      {
        Header: fieldLabels.validationMessage,
        id: "default-validationMessage",
        accessor: (row) => (
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={row.validationMessage}
          >
            {row.validationMessage || NotSet}
          </div>
        ),
        minWidth: 100,
        width: 300,
        align: "left",
      },
      {
        Header: fieldLabels.predefinedOptions,
        id: "default-predefinedOptions",
        accessor: (row) =>
          !!row.predefinedOptions ? (
            <TableArrayRenderer<string> values={row.predefinedOptions} arrayLength={5}>
              {(v) => v}
            </TableArrayRenderer>
          ) : (
            NotSet
          ),
        minWidth: 100,
        width: 300,
        align: "left",
      },
      {
        Header: fieldLabels.predefinedOptionsFromValues,
        id: "default-predefinedOptionsFromValues",
        accessor: (row) => <ToggleIndicator enabled={row.predefinedOptionsFromValues ?? false} labelSet="yes/no" />,
        minWidth: 100,
        width: 150,
        align: "left",
      },
      {
        Header: fieldLabels.defaultValues,
        id: "default-defaultValues",
        accessor: (row) =>
          !!row.defaultValues ? (
            <CustomFieldTableRenderer
              entity={row}
              title={fieldLabels.defaultValues}
              customfield={row as CustomField}
              value={row.defaultValues}
              includeCustomFieldId={false}
            />
          ) : (
            NotSet
          ),
        minWidth: 100,
        width: 300,
        align: "left",
      },
      {
        Header: fieldLabels.integrations,
        id: "default-integrations",
        accessor: (row) => (
          <TableArrayRenderer values={row.integrations} arrayLength={5}>
            {(e) => <span className="label label-soft-default">{e}</span>}
          </TableArrayRenderer>
        ),
        minWidth: 100,
        width: 200,
        align: "left",
      },
      {
        id: "default-enteredOn",
        Header: fieldLabels.enteredOn,
        accessor: (row) => <DateTimeRenderer date={row.enteredOn} includeElapsed={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("ENTERED_ON", sort, setSort),
      },
      {
        id: "default-enteredBy",
        Header: fieldLabels.enteredBy,
        accessor: (row) => <GetPersons persons={row.enteredBy} createLinks={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("ENTERED_BY", sort, setSort),
      },
      {
        id: "default-modifiedOn",
        Header: fieldLabels.modifiedOn,
        accessor: (row) => <DateTimeRenderer date={row.modifiedOn} includeElapsed={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("MODIFIED_ON", sort, setSort),
      },
      {
        id: "default-modifiedBy",
        Header: fieldLabels.modifiedBy,
        accessor: (row) => <GetPersons persons={row.modifiedBy} createLinks={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("MODIFIED_BY", sort, setSort),
      },
    ];

    return headers;
  }, [
    entityConstants.icon,
    fieldLabels.dataType,
    fieldLabels.defaultValues,
    fieldLabels.description,
    fieldLabels.enteredBy,
    fieldLabels.enteredOn,
    fieldLabels.predefinedOptions,
    fieldLabels.predefinedOptionsFromValues,
    fieldLabels.id,
    fieldLabels.integrations,
    fieldLabels.modifiedBy,
    fieldLabels.modifiedOn,
    fieldLabels.name,
    fieldLabels.placeholder,
    fieldLabels.readOnly,
    fieldLabels.required,
    fieldLabels.validationMessage,
    fieldLabels.validationRegexp,
    setSort,
    sort,
  ]);
  return { columns };
};
export const CustomFieldsTable = ({
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
}: EntityTableProps<"customFields">) => {
  const history = useHistory();
  const [showModal, setShowModal] = useState(false);
  const { bulkDeleteMutationAsync: deleteCustomField, isLoadingBulkDeleteMutation } = entityApi;

  const { defaults, tabStoreDefaults } = useCustomFieldsTableDefaults({ fieldLabels });

  const { selection, resultsCount, selectionPermissions, onCountChange, onSelectionChange, onSelectionPermissions } =
    useGenericVirtualizedTable<CustomField>();

  const {
    filters,
    sidebarFilters,
    forcedFilters,
    columnSetting,
    columnWidths,
    customTabs,
    fixedTabs,
    temporaryTabs,
    dispatchTabStore,
    currentTab,
    tabsLoading,
    tabsModified,
  } = useTabStore<CustomField, CustomFieldFilters, CustomFieldFilterForm>({
    resource: entityConstants.resource,
    defaults: tabStoreDefaults,
  });

  const { functionRef, onTabChange, sort, setSort, searchValue, setSearchValue } = useGenericVirtualizedTableTabs({
    currentTab,
    tabsLoading,
    filters,
    switchSortState: switchCustomFieldsDefaultSortState,
    dispatchTabStore,
  });

  const { columns } = useCustomFieldsTableColumns({ entityConstants, fieldLabels, sort, setSort });

  const memoizedFilters = useMemo(() => ({ ...filters, ...forcedFilters }), [filters, forcedFilters]);

  return (
    <TableView>
      <TableView.Head>
        <TableView.Head.Label>
          <TableViewLabel entityConstants={entityConstants} resultsCount={resultsCount} />
        </TableView.Head.Label>
        <TableView.Head.Controls>
          <button
            className="btn btn-primary"
            title={`Add ${entityConstants.entitySingular}`}
            onClick={() => history.push(routes.getAddRoute)}
            disabled={!permissions.canCreate}
          >
            <LucideIcon name="plus" /> Add {entityConstants.entitySingular}
          </button>
        </TableView.Head.Controls>
      </TableView.Head>
      <TableView.Body>
        <TableView.Body.Sidebar>
          <CustomFieldsFilterBar
            initialValues={sidebarFilters}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            currentTab={currentTab}
          />
        </TableView.Body.Sidebar>
        <TableView.Body.Content>
          <EntityTableTabs
            currentTab={currentTab}
            onTabChange={onTabChange}
            temporaryTabs={temporaryTabs}
            fixedTabs={fixedTabs}
            filters={filters}
            sidebarFilters={sidebarFilters}
            columnSetting={columnSetting}
            columnWidths={columnWidths}
            customTabs={customTabs}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            tabsModified={tabsModified}
          />
          <EntityTable>
            <EntityTable.Controls
              style={{
                borderTop: "0px",
                borderRadius: "0px",
              }}
            >
              <EntityFilterIndicator<CustomField, CustomFieldFilters>
                filters={filters}
                excludeFilters={{}}
                translatorConsts={CustomFieldFiltersTranslator}
              />
              <SearchInput searchValue={searchValue} setSearchValue={setSearchValue} placeholder="Search" />
              <EntityTableEditButton
                entityConstants={entityConstants}
                routes={routes}
                permissions={permissions}
                selection={selection}
                selectionPermissions={selectionPermissions}
              />
              <MoreDropdown drop="right" btn="btn btn-ghost-secondary">
                <li>
                  <EntityTableCloneButton
                    entityConstants={entityConstants}
                    routes={routes}
                    permissions={permissions}
                    selection={selection}
                  />
                </li>
                <li>
                  <EntityTableDeleteButton
                    entityConstants={entityConstants}
                    permissions={permissions}
                    selection={selection}
                    selectionPermissions={selectionPermissions}
                    onClick={() => setShowModal(true)}
                  />
                </li>
              </MoreDropdown>
              <AlertModal
                type={"danger"}
                showModal={showModal}
                setShowModal={setShowModal}
                title={`${
                  selection.size === 0
                    ? `Select ${entityConstants.entitySingular} to delete`
                    : selection.size === 1
                    ? `Delete the selected ${entityConstants.entitySingular}?`
                    : `Delete the selected ${entityConstants.entityPlural}?`
                }`}
                description={`Proceeding will permanently delete the selected ${entityConstants.entityPlural}.`}
                proceedLabel={"Delete"}
                onProceed={async () => {
                  await deleteCustomField({
                    ids: Array.from(selection),
                    goBackOnSuccess: false,
                    showToast: false,
                    entityName: entityConstants.resource,
                    params: { deletePermanently: true },
                  }).catch((e) => {});
                  functionRef.current?.resetSelection();
                  setShowModal(false);
                }}
                loading={isLoadingBulkDeleteMutation}
              />
            </EntityTable.Controls>
            <EntityTable.Body<CustomField, CustomFieldFilters>
              functionRef={functionRef}
              entityConstants={entityConstants}
              filters={memoizedFilters}
              columns={columns}
              columnSelect
              columnSetting={columnSetting}
              columnWidths={columnWidths}
              defaultColumnSettings={defaults}
              dispatchTabStore={dispatchTabStore}
              setResultsCount={onCountChange}
              onSelectionChange={onSelectionChange}
              onSelectionPermissions={onSelectionPermissions}
              showPermissionColumn
              loading={tabsLoading}
            />
          </EntityTable>
        </TableView.Body.Content>
      </TableView.Body>
    </TableView>
  );
};
